import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

import { IconArrowLeft } from "@frameio/vapor-icons/build/esm/icons/IconArrowLeft";
import { IconButton } from "../Button";
import { ScrollContainer } from "../ScrollContainer";
import { useLazyLoad } from "../Tabs/useLazyLoad";
import { Text } from "../Text";
import { easeCurves } from "../../motion/eases";
import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { theme } from "../../theme";
import { isFunction } from "../../utils/is-function";
/**
 * DrillDown is an iOS style navigation view, which allows
 * acts as a stack navigator.
 */

export function DrillDown(_ref) {
  var value = _ref.value,
      _ref$backLabel = _ref.backLabel,
      backLabel = _ref$backLabel === void 0 ? 'Go back' : _ref$backLabel,
      renderPanelActionsProp = _ref.renderPanelActions,
      columns = _ref.columns,
      _ref$lazyLoad = _ref.lazyLoad,
      lazyLoad = _ref$lazyLoad === void 0 ? true : _ref$lazyLoad,
      onChange = _ref.onChange,
      _ref$titleBackgroundC = _ref.titleBackgroundColor,
      titleBackgroundColor = _ref$titleBackgroundC === void 0 ? 'grey15' : _ref$titleBackgroundC;
  /**
   * Panel actions are interactive elements which appear in the
   * top-right side of the title area.
   */

  var panelActions = useMemo(function () {
    if (!renderPanelActionsProp) {
      return null;
    }

    var nodes = renderPanelActionsProp(value, onChange);
    return /*#__PURE__*/React.createElement(PanelActions, {
      style: {
        opacity: nodes ? 1 : 0
      }
    }, nodes);
  }, [onChange, renderPanelActionsProp, value]);
  var onBackClick = useCallback(function () {
    onChange(value - 1);
  }, [onChange, value]);
  var backButtonStyle = useMemo(function () {
    return getBackButtonStyle(value);
  }, [value]);

  var _useLazyLoad = useLazyLoad({
    lazyLoad: lazyLoad,
    itemCount: columns.length,
    currentPage: value
  }),
      shouldLoadIndex = _useLazyLoad.shouldLoadIndex;

  return /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(TitleContainer, {
    $backgroundColor: titleBackgroundColor
  }, /*#__PURE__*/React.createElement(BackButton, {
    style: backButtonStyle
  }, /*#__PURE__*/React.createElement(IconButton, {
    variant: "link",
    onClick: onBackClick,
    icon: /*#__PURE__*/React.createElement(IconArrowLeft, null),
    label: backLabel
  })), columns.map(function (column, i) {
    return column.back && /*#__PURE__*/React.createElement(BackLabelContainer, {
      key: i,
      style: getBackTitleStyle(i, value)
    }, shouldLoadIndex(i) && /*#__PURE__*/React.createElement(Text, {
      color: "body",
      variant: "primary"
    }, isFunction(column.back) ? column.back({
      goBack: onBackClick
    }) : column.back));
  }), columns.map(function (column, i) {
    return column.title && /*#__PURE__*/React.createElement(PrimaryTitleContainer, {
      key: i,
      style: getPrimaryTitleStyle(i, value, column.titleVisible)
    }, shouldLoadIndex(i) && /*#__PURE__*/React.createElement(Text, {
      color: "body",
      variant: "primary"
    }, isFunction(column.title) ? column.title() : column.title));
  }), panelActions), /*#__PURE__*/React.createElement(Content, null, columns.map(function (item, i) {
    return /*#__PURE__*/React.createElement(Column, {
      style: getAnimationValue(i, value),
      key: i
    }, shouldLoadIndex(i) ? renderFunction(item.content) : null);
  })));
}

function renderFunction(node) {
  return isFunction(node) ? node() : node;
}

export var DrillDownScrollingColumn = function DrillDownScrollingColumn(_ref2) {
  var onScrollProp = _ref2.onScroll,
      backgroundColor = _ref2.backgroundColor,
      children = _ref2.children,
      _ref2$paddingX = _ref2.paddingX,
      paddingX = _ref2$paddingX === void 0 ? 0 : _ref2$paddingX;
  return /*#__PURE__*/React.createElement(ScrollContainer, {
    onScroll: onScrollProp,
    backgroundColor: backgroundColor,
    enableBottomBorder: false,
    paddingX: paddingX
  }, children);
};
/**
 * Animations
 */

var hiddenStyles = {
  visibility: 'hidden',
  opacity: 0
};
var visibleStyles = {
  visibility: 'visible',
  opacity: 1
};
/**
 * Primary content animations
 */

function getAnimationValue(i, currentIndex) {
  // current
  if (i === currentIndex) {
    return _objectSpread({
      transform: "translateX(0%)"
    }, visibleStyles);
  } // next


  if (i > currentIndex) {
    return _objectSpread({
      transform: "translateX(15%)"
    }, hiddenStyles);
  } // previous


  return {
    transform: "translateX(-10%)",
    opacity: 0
  };
}
/**
 * Back button animations
 */


function getBackButtonStyle(i) {
  return i === 0 ? hiddenStyles : visibleStyles;
}
/**
 * Back title animations
 */


function getBackTitleStyle(i, currentIndex) {
  // current
  if (i === currentIndex) {
    return _objectSpread({}, visibleStyles);
  } // next


  if (i > currentIndex) {
    return _objectSpread({}, hiddenStyles);
  } // previous


  return _objectSpread({}, hiddenStyles);
}
/**
 * Primary title animations
 */


function getPrimaryTitleStyle(i, currentIndex) {
  var visible = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true; // current

  if (i === currentIndex) {
    return _objectSpread({
      transform: "translateX(0%)"
    }, visible ? visibleStyles : hiddenStyles);
  } // next


  if (i > currentIndex) {
    return _objectSpread({
      transform: "translateX(15%)"
    }, hiddenStyles);
  } // previous


  return _objectSpread({
    transform: "translateX(-10%)"
  }, hiddenStyles);
}

var transition = css(["transition-duration:0.35s;transition-timing-function:", ";transition-property:transform,opacity,visibility;"], easeCurves.primary);
var Container = styled.div.withConfig({
  displayName: "DrillDown__Container",
  componentId: "vapor__sc-128tzk0-0"
})(["overflow:hidden;display:flex;height:100%;flex:1;flex-direction:column;"]);
var TitleContainer = styled.div.withConfig({
  displayName: "DrillDown__TitleContainer",
  componentId: "vapor__sc-128tzk0-1"
})(["display:flex;align-items:center;justify-content:space-between;padding:", ";"], theme.spaces[12]);
var PrimaryTitleContainer = styled.div.withConfig({
  displayName: "DrillDown__PrimaryTitleContainer",
  componentId: "vapor__sc-128tzk0-2"
})(["position:absolute;display:flex;width:100%;left:0;padding-left:", ";padding-right:", ";align-items:center;pointer-events:none;justify-content:center;", ";"], theme.spaces[48], theme.spaces[48], transition);
var BackLabelContainer = styled.div.withConfig({
  displayName: "DrillDown__BackLabelContainer",
  componentId: "vapor__sc-128tzk0-3"
})(["position:absolute;left:", ";", ";"], theme.spaces[48], transition);
var Content = styled.div.withConfig({
  displayName: "DrillDown__Content",
  componentId: "vapor__sc-128tzk0-4"
})(["position:relative;overflow:hidden;flex:1;width:100%;"]);
var Column = styled.div.withConfig({
  displayName: "DrillDown__Column",
  componentId: "vapor__sc-128tzk0-5"
})(["position:absolute;top:0;left:0;right:0;bottom:0;height:100%;", ";will-change:transform,opacity;&:before{content:'';position:absolute;top:0;left:0;right:0;bottom:0;height:100%;box-shadow:0 0 12px -2px rgba(0,0,0,0.2);}"], transition);
var BackButton = styled.div.withConfig({
  displayName: "DrillDown__BackButton",
  componentId: "vapor__sc-128tzk0-6"
})(["", ";"], transition);
var PanelActions = styled.div.withConfig({
  displayName: "DrillDown__PanelActions",
  componentId: "vapor__sc-128tzk0-7"
})(["", ";will-change:transform;"], transition);